import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { DollarSign, TrendingUp, Clock, CheckCircle } from 'lucide-react';
import CRMLayout from '../../components/CRM/CRMLayout';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STAGE_COLORS = {
  submitted: 'bg-purple-500', approved: 'bg-green-500',
  docs_out: 'bg-teal-500', settled: 'bg-emerald-500'
};

const STAGE_LABELS = {
  submitted: 'Submitted', approved: 'Approved',
  docs_out: 'Docs Out', settled: 'Settled'
};

export default function Commissions() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/crm/commission-report`);
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching commission report:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <CRMLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
        </div>
      </CRMLayout>
    );
  }

  const totals = report?.totals || { estimated: 0, actual: 0, pending: 0 };
  const deals = report?.deals || [];

  return (
    <CRMLayout>
      <div className="pb-20">
        {/* Header */}
        <div className="bg-navy-800 border-b border-white/5">
          <div className="px-6 py-6">
            <h1 className="text-3xl font-heading text-white uppercase tracking-wide" data-testid="commissions-title">
              Commission Tracker
            </h1>
            <p className="text-gray-400 mt-1">Track your earnings across all deals</p>
          </div>
        </div>

        <div className="px-6 py-8 space-y-8">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/5 border border-emerald-500/30 rounded-xl p-6" data-testid="commission-earned">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Earned</span>
                <CheckCircle className="h-5 w-5 text-emerald-400" />
              </div>
              <p className="text-3xl font-heading text-white">${totals.actual.toLocaleString('en-AU')}</p>
              <p className="text-emerald-400 text-sm mt-1">Settled deals</p>
            </div>
            <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/5 border border-blue-500/30 rounded-xl p-6" data-testid="commission-estimated">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Estimated Pipeline</span>
                <TrendingUp className="h-5 w-5 text-blue-400" />
              </div>
              <p className="text-3xl font-heading text-white">${totals.estimated.toLocaleString('en-AU')}</p>
              <p className="text-blue-400 text-sm mt-1">Active deals</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/5 border border-yellow-500/30 rounded-xl p-6" data-testid="commission-pending">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Pending</span>
                <Clock className="h-5 w-5 text-yellow-400" />
              </div>
              <p className="text-3xl font-heading text-white">${totals.pending.toLocaleString('en-AU')}</p>
              <p className="text-yellow-400 text-sm mt-1">Awaiting settlement</p>
            </div>
          </div>

          {/* Commission Table */}
          <div className="bg-navy-800 rounded-xl border border-white/5 overflow-hidden" data-testid="commission-table">
            <div className="p-6 border-b border-white/5">
              <h2 className="text-white font-semibold">Deal Commissions</h2>
            </div>
            {deals.length === 0 ? (
              <div className="p-12 text-center">
                <DollarSign className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No deals with commission data yet</p>
                <p className="text-gray-500 text-sm mt-1">Commission tracking starts when deals reach the Submitted stage</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-navy-900">
                    <tr className="text-left text-gray-400 text-sm">
                      <th className="p-4">Client</th>
                      <th className="p-4">Lender</th>
                      <th className="p-4">Amount</th>
                      <th className="p-4">Stage</th>
                      <th className="p-4">Est. Commission</th>
                      <th className="p-4">Actual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deals.map((deal) => (
                      <tr key={deal.deal_id} className="border-t border-white/5 hover:bg-navy-900/50">
                        <td className="p-4">
                          <Link to={`/crm/deals/${deal.deal_id}`} className="text-white hover:text-purple-400">
                            {deal.client_name}
                          </Link>
                          <p className="text-gray-500 text-xs">{deal.business_name}</p>
                        </td>
                        <td className="p-4 text-gray-400">{deal.lender || '-'}</td>
                        <td className="p-4 text-white">${deal.amount.toLocaleString('en-AU')}</td>
                        <td className="p-4">
                          <span className={`inline-block px-2 py-1 rounded text-xs text-white ${STAGE_COLORS[deal.stage] || 'bg-gray-500'}`}>
                            {STAGE_LABELS[deal.stage] || deal.stage}
                          </span>
                        </td>
                        <td className="p-4 text-blue-400">
                          {deal.estimated_commission > 0 ? `$${deal.estimated_commission.toLocaleString('en-AU')}` : '-'}
                        </td>
                        <td className="p-4 text-emerald-400">
                          {deal.actual_commission > 0 ? `$${deal.actual_commission.toLocaleString('en-AU')}` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </CRMLayout>
  );
}
